import { useEffect, useState } from 'react';
import { inventoryApi, type Forecast, type InventoryHealth, type TransferOrder } from '../../api/inventory';

export function StockTransferPage() {
  const [transfers, setTransfers] = useState<TransferOrder[]>([]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const reload = () => void inventoryApi.listTransfers().then(setTransfers).catch((e) => setError(String(e)));

  useEffect(() => { reload(); }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await inventoryApi.createTransfer({
        from_warehouse_id: fromId,
        to_warehouse_id: toId,
        items: [{ product_id: productId, quantity: Number(quantity) }],
      });
      setMessage('Transfer order created.');
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Stock Transfer</h1>
      <form onSubmit={onSubmit} className="mt-6 grid gap-3 rounded-xl border bg-white p-4 sm:grid-cols-2">
        <input className="rounded border px-3 py-2 text-sm" placeholder="From warehouse ID" value={fromId} onChange={(e) => setFromId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="To warehouse ID" value={toId} onChange={(e) => setToId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Product ID" value={productId} onChange={(e) => setProductId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white sm:col-span-2">Create transfer</button>
      </form>
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <ul className="mt-6 space-y-2 text-sm">
        {transfers.map((t) => (
          <li key={t.id} className="rounded border bg-white p-3">
            {t.from_warehouse_id} → {t.to_warehouse_id} · {t.status} · {t.items?.length ?? 0} items
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReservationManagerPage() {
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reservationId, setReservationId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const onReserve = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const res = await inventoryApi.reserveInventory({ product_id: productId, warehouse_id: warehouseId, quantity: Number(quantity) });
      setReservationId(res.id);
      setMessage(`Reserved ${res.quantity} units (${res.status}).`);
    } catch (err) {
      setError(String(err));
    }
  };

  const onRelease = async () => {
    if (!reservationId) return;
    setError('');
    try {
      await inventoryApi.releaseInventory(reservationId);
      setMessage('Reservation released.');
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Reservation Manager</h1>
      <form onSubmit={onReserve} className="mt-6 grid gap-3 rounded-xl border bg-white p-4 sm:grid-cols-2">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Product ID" value={productId} onChange={(e) => setProductId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Warehouse ID" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Reserve stock</button>
      </form>
      {reservationId && (
        <button type="button" className="mt-4 rounded border px-4 py-2 text-sm" onClick={() => void onRelease()}>Release {reservationId}</button>
      )}
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function AllocationManagerPage() {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Allocation Manager</h1>
      <p className="mt-2 text-sm text-slate-600">Confirm reservations into allocations when orders are ready to ship. Use the reservation manager to create holds, then confirm via transfer or fulfillment workflows.</p>
    </div>
  );
}

export function CycleCountPage() {
  const [warehouseId, setWarehouseId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const c = await inventoryApi.createCycleCount({ warehouse_id: warehouseId });
      setMessage(`Cycle count ${c.id} opened (${c.status}).`);
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Cycle Count</h1>
      <form onSubmit={onSubmit} className="mt-6 flex flex-wrap gap-3 rounded-xl border bg-white p-4">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Warehouse ID" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Start cycle count</button>
      </form>
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function PhysicalVerificationPage() {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Physical Verification</h1>
      <p className="mt-2 text-sm text-slate-600">Record counted quantities during cycle counts and reconcile variances against expected on-hand balances.</p>
    </div>
  );
}

export function InventoryForecastPage() {
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [error, setError] = useState('');

  const onLoad = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const f = await inventoryApi.forecast({ product_id: productId, warehouse_id: warehouseId, horizon_days: '30' });
      setForecast(f);
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Demand Forecast</h1>
      <form onSubmit={onLoad} className="mt-6 flex flex-wrap gap-3 rounded-xl border bg-white p-4">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Product ID" value={productId} onChange={(e) => setProductId(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Warehouse ID" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Generate forecast</button>
      </form>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {forecast && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Predicted demand', forecast.predicted_demand],
            ['Reorder point', forecast.reorder_point],
            ['Suggested qty', forecast.suggested_qty],
            ['Confidence', forecast.confidence],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl border bg-white p-4">
              <p className="text-sm text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold">{String(value)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function InventoryHealthPage() {
  const [health, setHealth] = useState<InventoryHealth | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void inventoryApi.health().then(setHealth).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Inventory Health</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {health && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['Health score', health.score],
            ['Stockout risk', health.stockout_risk],
            ['Overstock risk', health.overstock_risk],
            ['Low stock SKUs', health.low_stock_count],
            ['Out of stock SKUs', health.out_of_stock_count],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl border bg-white p-4">
              <p className="text-sm text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold">{String(value)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
